/**
 * RELATION PANEL — Pannello relazioni polimorfiche.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useRelationFilters } from '../plugin/useRelationFilters';
import {
  deleteBidirectionalRelationship,
  fetchRelationships,
  setRelationshipPrimary,
} from '../entities/relationship/relationshipThunks';
import { resolveEntityLabel, getEntityDetailPath, getEntityType, getEntityFqn, entityTypeLabelPlural } from '../entityRegistry/entityRegistry';
import { AddRelationDrawer } from './AddRelationDrawer';
import { EditRelationDescriptionsModal } from './EditRelationDescriptionsModal';
import { ConfirmDialog } from './ConfirmDialog';
import { useConfirmDialog } from './useConfirmDialog';
import { useRelationPanelGroup } from './relationPanelGroupContext';
import type { Relationship } from '../entities/relationship/relationshipTypes';
import { selectRelationshipsByTargetType } from '../entities/relationship/relationshipSlice';

/**
 * Argomenti passati al renderItem del RelationPanel. Il consumer puo' usarli
 * per decidere come mostrare la card (default: link + descrizione) oppure
 * per renderizzare una card personalizzata (es. BusinessRelationCard).
 */
export interface RelationItemContext {
  relationship: Relationship;
  /** Label risolta dal registry (es. "Acme Srl"). */
  label: string;
  /** Path client alla pagina dettaglio. */
  path: string;
  /**
   * true se la relazione e' la "principale" dal POV dell'entita' sorgente
   * verso la classe target. Riflette direttamente `relationship.primary`
   * dal backend (TS-15): max 1 record con primary=true per ogni coppia
   * (groupId, className1, classPK1, className2). Puo' essere 0 record:
   * la lista resta senza primary finche' l'utente non assegna.
   */
  isPrimary: boolean;
  /** Apre il confirm dialog di delete (gestito da RelationPanel). */
  requestDelete: () => void;
  /**
   * Toggle del flag primary. Dispatch `setRelationshipPrimary` con il
   * payload corretto (id + nuovo valore + className/classPK per il
   * demote locale dei sibling). Fire-and-forget: lo store riflette
   * lo stato finale via extraReducer.
   *
   * **Optional**: il `RelationPanel` lo passa solo fra persone e aziende, dove
   * il "principale" ha un significato per ciascun verso: da Person→Business è
   * l'azienda principale del contatto, da Business→Person il contatto preferito
   * dell'azienda (FILO-25). Sono due flag diversi, uno per record della coppia.
   * Negli altri scenari (Person→Person, Quotation→*, da plugin verso core, ecc.)
   * e sulle righe `reverseOnly` il context NON include `requestSetPrimary` e le
   * card non renderizzano la stellina. Il flag `primary` resta comunque
   * persistito lato backend, semplicemente non è editabile in quel contesto UI.
   */
  requestSetPrimary?: (next: boolean) => void;
  /**
   * Apre la modale che modifica le due descrizioni del legame (FILO-20).
   *
   * **Optional**: il `RelationPanel` lo passa solo per i legami fra persone e
   * aziende (azienda ↔ persona, persona ↔ persona), dove la descrizione è il
   * ruolo e cambia nel tempo. Negli altri scenari è undefined e le card non
   * mostrano la matita.
   */
  requestEdit?: () => void;
}

interface RelationPanelProps {
  sourceType: string;
  sourceId: number;
  targetType: string;
  title?: string;
  onRelationChanged?: () => void;
  /**
   * Renderer personalizzato per ciascuna card. Se assente, RelationPanel
   * usa il layout default (label linkata + descrizione + bottone X).
   */
  renderItem?: (ctx: RelationItemContext) => ReactNode;
  /**
   * Se presente, renderizza un bottone "+ Crea" prima del bottone
   * "+ Aggiungi" (link esistente). Il click chiama questa callback —
   * usato dai Detail per portare l'utente alla form di creazione
   * dedicata di una target class plugin (es. Quotation) con query
   * string di pre-fill.
   */
  onCreateClick?: () => void;
  /**
   * Se true, nasconde il bottone "+ Aggiungi" e disabilita l'apertura
   * del AddRelationDrawer. Usato in combinazione con `onCreateClick`
   * quando il workflow "collega esistente" non ha senso per quella
   * coppia (es. PersonDetail -> Quotation in TS-18).
   */
  hideLinkExisting?: boolean;
}

interface ResolvedRelation {
  relationship: Relationship;
  label: string;
  path: string;
  /**
   * L'entità collegata non è stata risolta: non esiste più, oppure l'utente
   * non ha il permesso di vederla. La relazione però ESISTE, e va mostrata -
   * vedi il commento sulla risoluzione più sotto.
   */
  unresolved?: boolean;
}

export function RelationPanel({ sourceType, sourceId, targetType, title, onRelationChanged, renderItem, onCreateClick, hideLinkExisting }: RelationPanelProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirmDlg = useConfirmDialog();
  // Le relazioni sono lette dal bucket della PROPRIA sorgente: due pannelli
  // montati sulla stessa pagina su sorgenti diverse non si contaminano.
  const sourceFqn = getEntityFqn(sourceType);
  const byTargetType = useAppSelector(selectRelationshipsByTargetType(sourceFqn, sourceId));
  const [resolved, setResolved] = useState<ResolvedRelation[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ResolvedRelation | null>(null);
  // Accordion controllato: parte espanso. Se il panel e' dentro un
  // RelationPanelGroup, reagisce al segnale "comprimi/espandi tutti";
  // resta comunque toggleabile singolarmente.
  const [expanded, setExpanded] = useState(true);
  const group = useRelationPanelGroup();

  const entityType = getEntityType(targetType);
  const displayTitle = title ?? entityTypeLabelPlural(entityType, t, targetType);
  // byTargetType e' indicizzato per FQN (come arriva dal backend); convertiamo
  // il targetType short ("Business") in FQN via registry.
  const targetFqn = getEntityFqn(targetType);
  // I plugin abilitati possono chiedere che certe righe non si vedano qui: su F2
  // un veterinario è una Person collegata all'azienda, ma sta nel suo riquadro e
  // non va mostrata anche fra i Contatti. La relazione NON viene toccata —
  // timeline e interrogazione inversa continuano a funzionare — cambia solo
  // che cosa mostra questo pannello.
  const nascosti = useRelationFilters(sourceType, targetType, sourceId);

  // Tutto dentro un solo useMemo: `byTargetType[targetFqn] ?? []` produce un
  // array nuovo a ogni render quando la chiave manca, e tenerlo fuori come
  // dipendenza vanificherebbe la memoizzazione senza che si veda.
  const relations = useMemo(() => {
    const tutte = targetFqn ? byTargetType[targetFqn] ?? [] : [];
    return nascosti.size === 0 ? tutte : tutte.filter((r) => !nascosti.has(r.classPK2));
  }, [byTargetType, targetFqn, nascosti]);

  // Avvisa se il targetType non e' registrato: il pannello mostrerebbe
  // silenziosamente zero relazioni senza segnale del bug al programmatore.
  useEffect(() => {
    if (!targetFqn) {
      console.warn(
        '[relationship] RelationPanel: targetType non registrato nel registry',
        { targetType }
      );
    }
  }, [targetType, targetFqn]);

  // Allinea lo stato expanded al toggle "comprimi/espandi tutti" del gruppo.
  // Reagisce solo al cambio di `signal` (non al valore): tra un toggle e
  // l'altro l'utente puo' aprire/chiudere il singolo panel liberamente.
  const groupSignal = group?.signal;
  useEffect(() => {
    if (!group || group.signal === 0) return;
    setExpanded(!group.collapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSignal]);

  // Includo `primary` e le descrizioni nella key cosi' il useEffect ri-risolve
  // label+path quando l'utente toggla il flag o modifica le descrizioni
  // (altrimenti `resolved` resta con il vecchio oggetto e la card non
  // rispecchia lo stato aggiornato).
  const relationsKey = relations
    .map((r) => `${r.relationshipId}:${r.classPK2}:${r.primary ? 1 : 0}:${r.description ?? ''}:${r.reverseDescription ?? ''}`)
    .join(',');

  useEffect(() => {
    if (!relationsKey) {
      setResolved([]);
      return;
    }
    let cancelled = false;
    async function resolve() {
      // Si rilegge dallo store — non da `relations` — per avere il valore più
      // fresco, ma il filtro dei plugin va riapplicato qui: senza, il badge
      // conterebbe le righe filtrate e la lista ne mostrerebbe altre. Visto:
      // «Contatti (1)» sopra due contatti.
      const tutte = targetFqn ? byTargetType[targetFqn] ?? [] : [];
      const currentRelations =
        nascosti.size === 0 ? tutte : tutte.filter((rel) => !nascosti.has(rel.classPK2));
      // Ogni elemento si risolve per conto suo e non rifiuta MAI.
      //
      // Prima era una `Promise.all` di promesse che potevano rifiutare, e
      // `resolveLabel` di quasi tutti i tipi fa una GET: bastava un 404 - una
      // relazione rimasta appesa a un record cancellato, e senza foreign key
      // succede da solo - perché l'intera Promise.all rifiutasse, `setResolved`
      // non venisse mai chiamata e la lista restasse vuota.
      //
      // Il titolo però conta `relations.length`, che viene dallo store e non da
      // quella chiamata: il pannello mostrava "Preventivi (1)" sopra una lista
      // vuota. E con cinque relazioni ne bastava una rotta per farle sparire
      // tutte e cinque.
      //
      // L'elemento non risolto si MOSTRA, marcato. Nasconderlo lo renderebbe
      // anche incancellabile dall'interfaccia, e una relazione appesa nel vuoto
      // è esattamente quella che l'utente vuole poter togliere.
      const results = await Promise.all(
        currentRelations.map(async (rel) => {
          const path = getEntityDetailPath(targetType, rel.classPK2);
          try {
            const label = await resolveEntityLabel(targetType, rel.classPK2);
            return { relationship: rel, label, path };
          } catch {
            return {
              relationship: rel,
              label: t('relationship.unresolvedItem', { id: rel.classPK2 }),
              path,
              unresolved: true,
            };
          }
        })
      );
      if (!cancelled) setResolved(results);
    }
    resolve();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationsKey, targetType, nascosti]);

  function handleDelete(r: ResolvedRelation) {
    dispatch(deleteBidirectionalRelationship({
      relationshipId: r.relationship.relationshipId,
      className1: r.relationship.className1,
      classPK1: r.relationship.classPK1,
      className2: r.relationship.className2,
      classPK2: r.relationship.classPK2,
    }));
  }

  function handleSetPrimary(r: ResolvedRelation, next: boolean) {
    dispatch(setRelationshipPrimary({
      relationshipId: r.relationship.relationshipId,
      primary: next,
      className1: r.relationship.className1,
      classPK1: r.relationship.classPK1,
      className2: r.relationship.className2,
    }));
  }

  // La stellina "principale" si espone solo fra persone e aziende, in entrambi i
  // versi: Person -> Business è l'azienda principale del contatto, Business ->
  // Person il contatto preferito dell'azienda (FILO-25). Il backend tiene al più
  // un principale per sorgente e tipo di destinazione, quindi accenderla su un
  // contatto la spegne sul precedente. Per gli altri accoppiamenti (Person <->
  // Person, plugin entities come Quotation, ecc.) il toggle non viene esposto.
  const showPrimaryToggle =
    (sourceType === 'Person' && targetType === 'Business') ||
    (sourceType === 'Business' && targetType === 'Person');

  // Le descrizioni si modificano solo fra persone e aziende (FILO-20): azienda ↔
  // persona e persona ↔ persona, dove la descrizione è un ruolo che cambia (il
  // capostalla, il titolare che passa l'azienda al figlio). Azienda ↔ azienda e
  // i legami con preventivi, attività e plugin restano come sono.
  const showEditDescriptions =
    (sourceType === 'Person' && (targetType === 'Business' || targetType === 'Person')) ||
    (sourceType === 'Business' && targetType === 'Person');

  // Il salvataggio delle due descrizioni non è atomico: se fallisce si rilegge
  // la sorgente, così la riga mostra quello che c'è davvero.
  function handleEditFailed() {
    if (sourceFqn) dispatch(fetchRelationships({ className: sourceFqn, classPK: sourceId }));
  }

  function handleCreated() {
    setDrawerOpen(false);
    onRelationChanged?.();
  }

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters elevation={0} className="panel-box">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="relation-header">
          <Typography className="relation-title">{displayTitle}</Typography>
          {/* Numero di elementi collegati come badge (al posto del vecchio
              bottone "Aggiungi", ora spostato sotto la lista). */}
          <span className="relation-count">{relations.length}</span>
        </Box>
      </AccordionSummary>
      <AccordionDetails className="panel-box__details">
        {resolved.length === 0 ? (
          <p className="text-muted text-sm">
            {t('relationship.noItems')}
          </p>
        ) : (
          <ul className="relation-list">
            {resolved.map((r) => {
              const isPrimary = r.relationship.primary;
              const requestDelete = () =>
                confirmDlg.requestConfirm(
                  t('relationship.removeConfirm', { label: r.label }),
                  () => handleDelete(r),
                );
              // `requestSetPrimary` esposto solo fra persone e aziende, e mai su
              // una riga `reverseOnly`: il suo relationshipId è il record
              // dell'altro verso, e il PATCH accenderebbe il flag sbagliato.
              const requestSetPrimary = showPrimaryToggle && !r.relationship.reverseOnly
                ? (next: boolean) => handleSetPrimary(r, next)
                : undefined;
              const requestEdit = showEditDescriptions ? () => setEditing(r) : undefined;
              // Custom renderer (BusinessRelationCard / PersonRelationCard)
              // quando il consumer lo fornisce; altrimenti layout default.
              if (renderItem) {
                return (
                  <li
                    key={r.relationship.relationshipId}
                    className={`relation-list__item${isPrimary ? ' is-primary' : ''}`}
                  >
                    {renderItem({
                      relationship: r.relationship,
                      label: r.label,
                      path: r.path,
                      isPrimary,
                      requestDelete,
                      requestSetPrimary,
                      requestEdit,
                    })}
                  </li>
                );
              }
              return (
                <li
                  key={r.relationship.relationshipId}
                  className={`relation-list__item${isPrimary ? ' is-primary' : ''}`}
                >
                  <div>
                    {/* Un elemento non risolto non è un link: la pagina di
                        destinazione darebbe lo stesso errore che ha impedito di
                        risolverlo. */}
                    {r.unresolved ? (
                      <span className="relation-link relation-link--unresolved">{r.label}</span>
                    ) : (
                      <Link to={r.path} className="relation-link">
                        {r.label}
                      </Link>
                    )}
                    {(r.relationship.reverseDescription || r.relationship.description) && (
                      <span className="relation-list__desc">
                        {/* Mostra il "ruolo dell'altro verso me" (reverseDescription)
                            se la rel è bidirezionale; fallback a description forward
                            quando l'inverse non c'è (rel monodirezionali / record
                            orfani). */}
                        {r.relationship.reverseDescription || r.relationship.description}
                      </span>
                    )}
                  </div>
                  <Box className="relation-list__actions">
                    {requestSetPrimary && (
                      <Switch
                        checked={isPrimary}
                        onChange={(_, v) => requestSetPrimary(v)}
                        size="small"
                        title={isPrimary ? t('relationship.unsetPrimary') : t('relationship.setPrimary')}
                      />
                    )}
                    <Button onClick={requestDelete}
                      variant="text" size="small" color="error" className="relation-delete-btn">
                      x
                    </Button>
                  </Box>
                </li>
              );
            })}
          </ul>
        )}
        {/* Azione in fondo (stile mockup .rel-add): "+ Crea" quando non c'è il
            drawer "collega esistente", altrimenti "+ Aggiungi" che lo apre. */}
        {onCreateClick && hideLinkExisting && (
          <button type="button" className="rel-add" onClick={onCreateClick}>
            {t('relationship.create')}
          </button>
        )}
        {!hideLinkExisting && (
          <button type="button" className="rel-add" onClick={() => setDrawerOpen(true)}>
            {t('relationship.add')}
          </button>
        )}
      </AccordionDetails>

      {editing && (
        <EditRelationDescriptionsModal
          relationship={editing.relationship}
          targetLabel={editing.label}
          onClose={() => setEditing(null)}
          onFailed={handleEditFailed}
        />
      )}

      {drawerOpen && (
        <AddRelationDrawer
          sourceType={sourceType}
          sourceId={sourceId}
          targetType={targetType}
          onClose={() => setDrawerOpen(false)}
          onCreated={handleCreated}
          onCreateNew={hideLinkExisting ? undefined : onCreateClick}
        />
      )}
      <ConfirmDialog open={confirmDlg.isOpen} message={confirmDlg.message} onConfirm={confirmDlg.confirm} onCancel={confirmDlg.cancel}
        title={t('relationship.removeTitle')} confirmLabel={t('relationship.removeLabel')} />
    </Accordion>
  );
}
